import React, { useState } from 'react';
import { Box, Container, Grid, Typography, useTheme, Chip, TextField, Alert, Snackbar } from '@mui/material';
import TopUtilityBar from '@/components/sections/TopUtilityBar';
import HeaderNav from '@/components/sections/HeaderNav';
import FooterSection from '@/components/sections/FooterSection';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';

export const CareersPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    role: 'Façade Engineer',
    experience: '',
    portfolioUrl: '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitted(true);
  };

  const whyInverika = [
    {
      title: 'Landmark Projects',
      icon: 'apartment',
      desc: 'Work on high-value commercial, residential, and public infrastructure packages for Brigade Group, SAP Labs, and BMRCL.',
    },
    {
      title: 'Integrated Experience',
      icon: 'precision_manufacturing',
      desc: 'Gain hands-on exposure across engineering design, in-house 7,000 sq.ft. manufacturing, site erection, and QA/QC.',
    },
    {
      title: 'ISO Certified Standards',
      icon: 'verified',
      desc: 'Operate under ISO 9001, 14001, 45001, and 50001 certified systems with an uncompromising zero-harm safety culture.',
    },
    {
      title: 'Professional Growth',
      icon: 'trending_up',
      desc: 'Competitive compensation, merit-based advancement, technical mentorship, and continuous professional development.',
    },
  ];

  const openPositions = [
    {
      title: 'Façade Engineer',
      experience: '2 – 8 Years',
      location: 'Bengaluru HQ / Sarjapur Yard',
      type: 'Full-Time',
      summary: 'Coordinate façade structural design, review shop drawings, perform material planning, and manage technical submittals for curtain walls, ACP cladding, and glazing packages.',
      skills: ['Curtain Walling', 'Structural Glazing', 'Aluminium Extrusions', 'AutoCAD', 'Structural Calculations'],
    },
    {
      title: 'Site Engineer (Civil & Façade Execution)',
      experience: '2 – 6 Years',
      location: 'Active Project Sites (Bengaluru)',
      type: 'Full-Time',
      summary: 'Supervise daily site execution activities, manage specialist subcontractors, monitor progress against Gantt schedules, and enforce site quality and ISO 45001 safety protocols.',
      skills: ['Civil Execution', 'Site Supervision', 'Subcontractor Management', 'Gantt Schedules', 'ISO 45001 Safety'],
    },
  ];

  return (
    <Box sx={{ backgroundColor: theme.palette.background.default, minHeight: '100vh', color: theme.palette.text.primary, transition: 'background-color 0.3s ease, color 0.3s ease' }}>
      <TopUtilityBar />
      <HeaderNav />

      {/* Hero Banner */}
      <Box sx={{ py: { xs: 10, md: 14 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} lg={7}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 0.75,
                  borderRadius: 1.5,
                  backgroundColor: isDark ? 'rgba(0, 102, 255, 0.15)' : 'rgba(0, 80, 203, 0.08)',
                  border: `1px solid ${isDark ? 'rgba(0, 102, 255, 0.3)' : 'rgba(0, 80, 203, 0.2)'}`,
                  mb: 3,
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16, color: theme.palette.primary.main }}>
                  work
                </span>
                <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  CAREERS AT INVERIKA
                </Typography>
              </Box>

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.25rem', sm: '3.25rem', md: '3.75rem' },
                  color: theme.palette.text.primary,
                  fontFamily: "'BankGothic Md BT', 'BankGothic MD BT', 'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  lineHeight: 1.15,
                  letterSpacing: '0.01em',
                  textTransform: 'uppercase',
                  mb: 3,
                }}
              >
                BUILD YOUR CAREER WITH AN<br />
                <Box component="span" sx={{ color: theme.palette.primary.main }}>
                  ENGINEERING LEADER.
                </Box>
              </Typography>

              <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontSize: { xs: '1rem', md: '1.05rem' }, lineHeight: 1.7, mb: 4, maxWidth: 600 }}>
                Join Inverika's multidisciplinary team of engineers, project managers, and construction specialists delivering landmark building envelopes, civil works, and infrastructure projects across India.
              </Typography>

              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  document.getElementById('openings')?.scrollIntoView({ behavior: 'smooth' });
                }}
                sx={{ textTransform: 'uppercase', fontWeight: 700, fontSize: '0.8125rem', px: 3, py: 1.25 }}
              >
                VIEW OPEN POSITIONS ↓
              </Button>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Card sx={{ p: 4, border: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="h6" sx={{ fontFamily: 'Space Grotesk', fontWeight: 700, mb: 2 }}>
                  Direct Recruitment Contact
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                  Send your updated CV and portfolio directly to our engineering talent acquisition team:
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 20, color: theme.palette.primary.main }}>
                      mail
                    </span>
                    <Typography variant="body2" component="a" href="mailto:careers@inverika.com" sx={{ color: theme.palette.text.primary, fontWeight: 600, textDecoration: 'none' }}>
                      careers@inverika.com
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 20, color: theme.palette.primary.main }}>
                      chat
                    </span>
                    <Typography variant="body2" component="a" href="https://wa.me/916366322532" target="_blank" rel="noopener noreferrer" sx={{ color: theme.palette.primary.main, fontWeight: 600, textDecoration: 'none' }}>
                      WhatsApp HR: +91-6366322532
                    </Typography>
                  </Box>
                </Box>

                <Alert severity="info" sx={{ fontSize: '0.8125rem' }}>
                  Please include your executed project portfolio and notice period when submitting your CV.
                </Alert>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Why Build Your Career at Inverika */}
      <Box sx={{ py: { xs: 10, md: 14 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', mb: 1 }}>
              WHY JOIN US
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, color: theme.palette.text.primary, fontFamily: 'Space Grotesk', fontWeight: 700 }}>
              Why Build Your Career at Inverika?
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {whyInverika.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{ p: 4, height: '100%', border: `1px solid ${theme.palette.divider}` }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 36, color: theme.palette.primary.main, marginBottom: 16 }}>
                    {item.icon}
                  </span>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 700, mb: 1.5, fontFamily: 'Space Grotesk', color: theme.palette.text.primary }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.875rem', lineHeight: 1.6 }}>
                    {item.desc}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Current Openings Section */}
      <Box id="openings" sx={{ py: { xs: 10, md: 14 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', mb: 1 }}>
              ACTIVE RECRUITMENT
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, color: theme.palette.text.primary, fontFamily: 'Space Grotesk', fontWeight: 700 }}>
              Current Technical Openings
            </Typography>
          </Box>

          <Grid container spacing={6}>
            <Grid item xs={12} lg={7}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {openPositions.map((pos, index) => (
                  <Card key={index} sx={{ p: 4, border: `1px solid ${theme.palette.divider}` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      <Box>
                        <Typography variant="h5" sx={{ fontSize: '1.35rem', fontWeight: 700, fontFamily: 'Space Grotesk', color: theme.palette.text.primary }}>
                          {pos.title}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                          {pos.location} • {pos.experience} Experience
                        </Typography>
                      </Box>
                      <Chip label={pos.type} size="small" sx={{ fontWeight: 700, backgroundColor: isDark ? 'rgba(0, 102, 255, 0.15)' : 'rgba(0, 80, 203, 0.1)', color: theme.palette.primary.main }} />
                    </Box>

                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.65, mb: 3 }}>
                      {pos.summary}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {pos.skills.map((skill, i) => (
                        <Chip key={i} label={skill} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                      ))}
                    </Box>
                  </Card>
                ))}
              </Box>
            </Grid>

            {/* Quick Application Form */}
            <Grid item xs={12} lg={5}>
              <Card sx={{ p: 4, border: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="h5" sx={{ fontFamily: 'Space Grotesk', fontWeight: 700, mb: 1 }}>
                  Submit Your Application
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                  Apply directly for active engineering positions:
                </Typography>

                <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <TextField label="Full Name" name="fullName" value={formData.fullName} onChange={handleChange} required fullWidth size="small" />
                  <TextField label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange} required fullWidth size="small" />
                  <TextField label="Phone / WhatsApp Number" name="phone" value={formData.phone} onChange={handleChange} required fullWidth size="small" />
                  <TextField label="Years of Experience" name="experience" value={formData.experience} onChange={handleChange} required fullWidth size="small" placeholder="e.g. 5 Years" />
                  <TextField label="Portfolio / LinkedIn Link" name="portfolioUrl" value={formData.portfolioUrl} onChange={handleChange} fullWidth size="small" />
                  <TextField label="Cover Note / Project Summary" name="message" value={formData.message} onChange={handleChange} multiline rows={3} fullWidth size="small" />

                  <Button variant="contained" color="primary" type="submit" sx={{ py: 1.25, fontWeight: 700 }}>
                    SUBMIT APPLICATION
                  </Button>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Snackbar Notification */}
      <Snackbar open={formSubmitted} autoHideDuration={6000} onClose={() => setFormSubmitted(false)}>
        <Alert severity="success" sx={{ width: '100%' }}>
          Application submitted successfully! Our HR team will contact you shortly.
        </Alert>
      </Snackbar>

      {/* Footer */}
      <FooterSection />
    </Box>
  );
};

export default CareersPage;
